import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { markovApi, agentApi, fishbowlApi } from '@/lib/markov-api';
import type {
  MarkovTrainRequest,
  MarkovSignal,
  MarkovDiagnostics,
  WalkForwardResult,
} from '@/types/markov.types';

// Markov Hooks

export function useMarkovSignal(instrument: string, timeframe: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['markov-signal', instrument, timeframe],
    queryFn: () => markovApi.getSignal(instrument, timeframe),
    enabled: enabled && !!instrument && !!timeframe,
    refetchInterval: 10000, // Refetch every 10 seconds
    staleTime: 5000,
  });
}

export function useMarkovDiagnostics(instrument: string, timeframe: string) {
  return useQuery({
    queryKey: ['markov-diagnostics', instrument, timeframe],
    queryFn: () => markovApi.getDiagnostics(instrument, timeframe),
    enabled: !!instrument && !!timeframe,
    staleTime: 60000, // Diagnostics don't change frequently
  });
}

export function useMarkovHealth() {
  return useQuery({
    queryKey: ['markov-health'],
    queryFn: () => markovApi.getHealth(),
    refetchInterval: 30000, // Check health every 30 seconds
  });
}

export function useMarkovTrain() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: MarkovTrainRequest) => markovApi.train(params),
    onSuccess: (data, variables) => {
      // Invalidate related queries to refetch
      queryClient.invalidateQueries({
        queryKey: ['markov-signal', variables.instrument, variables.timeframe]
      });
      queryClient.invalidateQueries({
        queryKey: ['markov-diagnostics', variables.instrument, variables.timeframe]
      });
      queryClient.invalidateQueries({ queryKey: ['markov-health'] });
    },
  });
}

export function useWalkForward() {
  return useMutation({
    mutationFn: ({
      instrument,
      timeframe,
      trainBars,
      testBars,
      numFolds
    }: {
      instrument: string;
      timeframe: string;
      trainBars?: number;
      testBars?: number;
      numFolds?: number;
    }) => markovApi.walkForward(instrument, timeframe, trainBars, testBars, numFolds),
  });
}

// Agent Hooks

export function useAgents() {
  return useQuery({
    queryKey: ['agents'],
    queryFn: () => agentApi.listAgents(),
    refetchInterval: 15000, // Refetch every 15 seconds
  });
}

export function useAgent(agentId: string) {
  return useQuery({
    queryKey: ['agent', agentId],
    queryFn: () => agentApi.getAgent(agentId),
    enabled: !!agentId,
    refetchInterval: 10000,
  });
}

export function useAgentDecisions(agentId: string, limit: number = 50) {
  return useQuery({
    queryKey: ['agent-decisions', agentId, limit],
    queryFn: () => agentApi.getDecisions(agentId, limit),
    enabled: !!agentId,
    refetchInterval: 5000, // Fast updates for recent decisions
  });
}

export function useAgentPerformance(agentId: string) {
  return useQuery({
    queryKey: ['agent-performance', agentId],
    queryFn: () => agentApi.getPerformance(agentId),
    enabled: !!agentId,
    refetchInterval: 30000,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.createAgent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useStartAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => agentApi.startAgent(agentId),
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

export function useStopAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => agentApi.stopAgent(agentId),
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
  });
}

// Fishbowl Hooks (Public, no auth)

export function useFishbowlDashboard() {
  return useQuery({
    queryKey: ['fishbowl-dashboard'],
    queryFn: () => fishbowlApi.getDashboard(),
    refetchInterval: 5000, // Fast updates for public view
  });
}

export function useFishbowlAgents() {
  return useQuery({
    queryKey: ['fishbowl-agents'],
    queryFn: () => fishbowlApi.getAgents(),
    refetchInterval: 5000,
  });
}

export function useFishbowlActivity(limit: number = 50) {
  return useQuery({
    queryKey: ['fishbowl-activity', limit],
    queryFn: () => fishbowlApi.getActivity(limit),
    refetchInterval: 3000, // Very fast for activity feed
  });
}

export function useFishbowlLeaderboard() {
  return useQuery({
    queryKey: ['fishbowl-leaderboard'],
    queryFn: () => fishbowlApi.getLeaderboard(),
    refetchInterval: 10000,
  });
}
